import { debounce } from "lodash";
import * as React from "react";
import { FC, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { actions, Form } from "react-redux-form";
import { useParams } from "react-router";
import Alert from "react-s-alert";
import { bindActionCreators } from "redux";
import * as MonitoringIssueDocumentActions from "../../../actions/monitoringIssueDocument";
import * as NavigationActions from "../../../actions/navigation";
import * as PagingActions from "../../../actions/pages/pagingPage";
import * as TrialInstitutionActions from "../../../actions/trialInstitution";
import { NotificationAlert } from "../../../components/common/index";
import { TextFormInput } from "../../../components/form/index";
import { RestrictedLayout } from "../../../components/layouts";
import { FontAwesomeIcon, FontAwesomeIcons } from "../../../constants/fontAwesomeIcons";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import { renderRegistrationFormSaveBlock } from "../../../helpers/alert";
import * as AuthenticationHelper from "../../../helpers/authentication";
import { TrialInstitutionMonitoringIssueDocumentCreateBreadcrumbs, TrialInstitutionMonitoringIssueDocumentDetailsBreadcrumbs } from "../../../helpers/breadcrumbHelper";
import { RegistrationComponent, renderFormControls } from "../../../helpers/formHelper";
import { MonitoringIssueDocumentSubMenu } from "../../../helpers/subMenuHelper";
import { useNavigate } from "../../../hooks/useNavigate";
import { RegistrationFormComponent } from "../../../interfaces/forms/IRegistrationComponent";
import { ITmdState } from "../../../reducers/index";
import { IRegistrationFormState } from "../../../reducers/reactReduxForms/index";
import { isUrl } from "src/functions/string";

import "./MonitoringIssueDocumentPage.scss";

interface MonitoringIssueDocumentPageProps {
    form: Dtos.MonitoringIssueDocument & IRegistrationFormState;
    formState: Dtos.FormState;
    formProperties: Dtos.FormProperty[];
    monitoringIssueDocument: Dtos.MonitoringIssueDocument;
    lookups: Dtos.Lookup[];
    loadingMonitoringIssueDocument: boolean;
    loadMonitoringIssueDocumentSuccess: boolean;
    loadMonitoringIssueDocumentFailure: boolean;
    loadMonitoringIssueDocumentErrorCode: string;
    savingMonitoringIssueDocument: boolean;
    saveMonitoringIssueDocumentSuccess: boolean;
    saveMonitoringIssueDocumentFailure: boolean;
    creatingMonitoringIssueDocument: boolean;
    createMonitoringIssueDocumentSuccess: boolean;
    createMonitoringIssueDocumentFailure: boolean;
    validatingMonitoringIssueDocument: boolean;
    validationFailures: Dtos.ResponseError[];
    permissions?: Dtos.PersonnelTmdPermissions[];
    trialInstitution: Dtos.TrialInstitution;
    loadingTrialInstitution: boolean;
    loadTrialInstitutionSuccess: boolean;
    loadTrialInstitutionFailure: boolean;
    loadTrialInstitutionErrorCode: string;
    pageSize: number;
    clearMonitoringIssueDocument: MonitoringIssueDocumentActions.IMonitoringIssueDocumentClearActionCreator;
    loadMonitoringIssueDocument: MonitoringIssueDocumentActions.IMonitoringIssueDocumentLoadByIdActionCreator;
    saveMonitoringIssueDocument: MonitoringIssueDocumentActions.IMonitoringIssueDocumentSaveActionCreator;
    createMonitoringIssueDocument: MonitoringIssueDocumentActions.IMonitoringIssueDocumentCreateActionCreator;
    validateMonitoringIssueDocument: MonitoringIssueDocumentActions.IMonitoringIssueDocumentValidateActionCreator;
    loadForm: typeof actions.load;
    changeForm: typeof actions.change;
    resetForm: typeof actions.reset;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
    clearTrialInstitution: TrialInstitutionActions.ITrialInstitutionClearActionCreator;
    loadTrialInstitution: TrialInstitutionActions.ITrialInstitutionLoadByIdActionCreator;
    setPageSize: PagingActions.IPagingPageSizeChangedStateActionCreator;
}

const reduxFormName: string = "reduxForms.monitoringIssueDocument";

const MonitoringIssueDocumentPage: FC<MonitoringIssueDocumentPageProps> = ({
    form,
    formState,
    formProperties,
    monitoringIssueDocument,
    lookups,
    loadingMonitoringIssueDocument,
    //loadMonitoringIssueDocumentSuccess,
    loadMonitoringIssueDocumentFailure,
    loadMonitoringIssueDocumentErrorCode,
    savingMonitoringIssueDocument,
    saveMonitoringIssueDocumentSuccess,
    saveMonitoringIssueDocumentFailure,
    creatingMonitoringIssueDocument,
    //createMonitoringIssueDocumentSuccess,
    //createMonitoringIssueDocumentFailure,
    validatingMonitoringIssueDocument,
    validationFailures,
    permissions,
    trialInstitution,
    loadingTrialInstitution,
    //loadTrialInstitutionSuccess,
    loadTrialInstitutionFailure,
    loadTrialInstitutionErrorCode,
    clearMonitoringIssueDocument,
    loadMonitoringIssueDocument,
    saveMonitoringIssueDocument,
    createMonitoringIssueDocument,
    validateMonitoringIssueDocument,
    loadForm,
    changeForm,
    resetForm,
    setBreadcrumbs,
    setDrawerItems,
    clearTrialInstitution,
    loadTrialInstitution,
}) => {
    //
    // -- Params --
    const { trialId } = useParams<{ trialId: string }>();
    const { trialInstitutionId } = useParams<{ trialInstitutionId: string }>();
    const { monitoringId } = useParams<{ monitoringId: string }>();
    const { monitoringIssueId } = useParams<{ monitoringIssueId: string }>();
    const { monitoringIssueDocumentId } = useParams<{ monitoringIssueDocumentId: string }>();
    const trialIdNumber = parseInt(trialId, 10);
    const trialInstitutionIdNumber = parseInt(trialInstitutionId, 10);
    const monitoringIdNumber = parseInt(monitoringId, 10);
    const monitoringIssueIdNumber = parseInt(monitoringIssueId, 10);
    const monitoringIssueDocumentIdNumber = parseInt(monitoringIssueDocumentId, 10);

    //
    // -- States --
    const [saveAndReturn, setSaveAndReturn] = useState<boolean>(false);
    const [formInitalised, setFormInitalised] = useState<boolean>(false);
    const [hasBeenSubmitted, setHasBeenSubmitted] = useState<boolean>(false);

    //
    // -- Hooks --
    const navigate = useNavigate();

    const loadReduxForm = () => {
        let extraData: Dtos.MonitoringIssueDocument = {} as Dtos.MonitoringIssueDocument;

        loadForm(
            reduxFormName,
            Object.assign({ ...monitoringIssueDocument }, extraData)
        );
    };

    // Load/Create monitoring document
    useEffect(() => {
        if (monitoringIssueDocumentIdNumber && monitoringIssueIdNumber && monitoringIdNumber && 
            trialInstitutionIdNumber && trialIdNumber && !monitoringIssueDocument && !loadingMonitoringIssueDocument
        ) {
            setBreadcrumbs(
                TrialInstitutionMonitoringIssueDocumentDetailsBreadcrumbs(
                    trialIdNumber, 
                    trialInstitutionIdNumber, 
                    monitoringIdNumber, 
                    monitoringIssueIdNumber,
                    monitoringIssueDocumentIdNumber
                )
            );

            loadMonitoringIssueDocument(monitoringIssueDocumentIdNumber);
        }
        else if (!monitoringIssueDocumentIdNumber && monitoringIssueIdNumber && monitoringIdNumber && 
            trialInstitutionIdNumber && trialIdNumber && !monitoringIssueDocument && !creatingMonitoringIssueDocument
        ) {
            setDrawerItems([]);
            setBreadcrumbs(
                TrialInstitutionMonitoringIssueDocumentCreateBreadcrumbs(
                    trialIdNumber, 
                    trialInstitutionIdNumber, 
                    monitoringIdNumber,
                    monitoringIssueIdNumber
                )
            );

            createMonitoringIssueDocument(monitoringIssueIdNumber);
        }
        else if (monitoringIssueDocument && !formInitalised) {
            loadReduxForm();
            setFormInitalised(true);
        }

        if (trialInstitutionIdNumber && !trialInstitution && !loadingTrialInstitution) {
            loadTrialInstitution(trialInstitutionIdNumber);
        }
    }, [
        monitoringIssueDocumentIdNumber,
        trialInstitutionIdNumber,
        trialIdNumber,
        monitoringIssueDocument,
        loadingMonitoringIssueDocument,
        creatingMonitoringIssueDocument,
        formInitalised,
        trialInstitution,
        loadingTrialInstitution,
        monitoringIdNumber,
        monitoringIssueIdNumber
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadMonitoringIssueDocumentFailure) {
            navigate(`/error/${loadMonitoringIssueDocumentErrorCode}`);
        }
        if (loadTrialInstitutionFailure) {
            navigate(`/error/${loadTrialInstitutionErrorCode}`);
        }
    }, [
        loadMonitoringIssueDocumentFailure,
        loadMonitoringIssueDocumentErrorCode,
        loadTrialInstitutionFailure,
        loadTrialInstitutionErrorCode
    ]);

    // Update breadcrumbs & drawer items
    useEffect(() => {
        if (monitoringIssueDocumentIdNumber && monitoringIssueIdNumber && monitoringIdNumber && 
            trialIdNumber && trialInstitutionIdNumber && trialInstitution && permissions
        ) {
            setDrawerItems(
                MonitoringIssueDocumentSubMenu(
                    trialIdNumber, 
                    trialInstitutionIdNumber, 
                    monitoringIdNumber, 
                    monitoringIssueIdNumber,
                    monitoringIssueDocumentIdNumber, 
                    permissions
                )
            );
            setBreadcrumbs(
                TrialInstitutionMonitoringIssueDocumentDetailsBreadcrumbs(
                    trialIdNumber,
                    trialInstitutionIdNumber,
                    monitoringIdNumber,
                    monitoringIssueIdNumber,
                    monitoringIssueDocumentIdNumber,
                    trialInstitution?.trial?.displayName,
                    trialInstitution?.institution?.name
                )
            );
        }
        else if (!monitoringIssueDocumentIdNumber && monitoringIssueIdNumber && 
            monitoringIdNumber && trialInstitutionIdNumber && trialIdNumber && trialInstitution
        ) {
            setDrawerItems([]);
            setBreadcrumbs(
                TrialInstitutionMonitoringIssueDocumentCreateBreadcrumbs(
                    trialIdNumber, 
                    trialInstitutionIdNumber, 
                    monitoringIdNumber,
                    monitoringIssueIdNumber,
                    trialInstitution?.trial?.displayName, 
                    trialInstitution?.institution?.name
                )
            );
        }
    }, [
        monitoringIssueDocumentIdNumber,
        monitoringIdNumber, 
        trialIdNumber,
        trialInstitutionIdNumber,
        trialInstitution,
        monitoringIssueDocument,
        permissions,
        monitoringIssueIdNumber
    ]);

    // Reload monitoring issue document after saving
    useEffect(() => {
        if (!savingMonitoringIssueDocument && saveMonitoringIssueDocumentSuccess) {
            Alert.success(
                <NotificationAlert
                    alertContent={<span>Monitoring Issue Document saved successfully</span>}
                    icon={FontAwesomeIcons.Solid.CHECK}
                />
            );

            if (saveAndReturn) {
                navigate(`/trials/${trialIdNumber}/trialinstitution/${trialInstitutionIdNumber}/monitoring/${monitoringIdNumber}/issues/${monitoringIssueIdNumber}/documents`);
            } else if (!monitoringIssueDocumentIdNumber) {
                navigate(`/trials/${trialIdNumber}/trialinstitution/${trialInstitutionIdNumber}/monitoring/${monitoringIdNumber}/issues/${monitoringIssueIdNumber}/documents/${monitoringIssueDocument.id}/details`);
                clearMonitoringIssueDocument();
                clearTrialInstitution();
            }
        }
    }, [
        savingMonitoringIssueDocument,
        saveMonitoringIssueDocumentSuccess
    ]);

    // Failed saving
    useEffect(() => {
        if (!savingMonitoringIssueDocument && saveMonitoringIssueDocumentFailure) {
            Alert.error(
                <NotificationAlert
                    alertContent={renderRegistrationFormSaveBlock()}
                    icon={FontAwesomeIcons.Solid.BAN}
                />
            );
        }
    }, [savingMonitoringIssueDocument, saveMonitoringIssueDocumentFailure]);

     // Cleanup effect to clear the monitoring issue document & trial institution on component unmount
    useEffect(() => {
        return () => {
            clearMonitoringIssueDocument();
            clearTrialInstitution();
            resetForm(reduxFormName);
        };
    }, [
        clearMonitoringIssueDocument, 
        clearTrialInstitution,
        resetForm, 
        reduxFormName
    ]);

    const handleOnSubmit = async (data: any) => {
        if (!hasBeenSubmitted) {
            setHasBeenSubmitted(true);
        }

        if (data) {
            let changes: Dtos.MonitoringIssueDocument = {} as Dtos.MonitoringIssueDocument;

            if (!data.monitoringIssueId) {
                changes.monitoringIssueId = monitoringIssueIdNumber;
            }

            await saveMonitoringIssueDocument(Object.assign({ ...data }, changes));

            if (data.saveAndReturn) {
                setSaveAndReturn(true);
            }
        }
    };

    const handleOnSubmitFailed = (_data: Dtos.MonitoringIssueDocument) => {
        Alert.error(
            <NotificationAlert
                alertContent={renderRegistrationFormSaveBlock()}
                icon={FontAwesomeIcons.Solid.BAN}
            />
        );
    };

    const onCancel = () => {
        navigate(`/trials/${trialIdNumber}/trialinstitution/${trialInstitutionIdNumber}/monitoring/${monitoringIdNumber}/issues/${monitoringIssueIdNumber}/documents`);
    };

    // Debounce validation after the initial submit to clear form validation errors as the form get updated.
    useEffect(() => {
        const debounceValidate = debounce(async () => {
            let changes: Dtos.MonitoringIssueDocument = {} as Dtos.MonitoringIssueDocument;

            if (!form.monitoringIssueId) {
                changes.monitoringIssueId = monitoringIssueIdNumber;
            }

            if (form && hasBeenSubmitted && !validatingMonitoringIssueDocument) {
                await validateMonitoringIssueDocument(Object.assign({ ...form }, changes));
            }
        }, 250);

        debounceValidate();

        return () => {
            debounceValidate.cancel();
        };
    }, [form]);

    const openFile = (link: string) => {
        window.open(`${link}`, "_blank");
        return null;
    };

    const renderContent = useMemo(() => {
        const hasPermission = AuthenticationHelper.hasPermission(
            permissions,
            Dtos.Permission.MonitoringIssueDocumentUpdate,
            undefined,
            undefined
        );

        if (hasPermission) {
            const formDisabled: boolean =
                !hasPermission ||
                savingMonitoringIssueDocument ||
                (formState && formState.formStatus == Dtos.FormStatus.Disabled);

            let formComponents: { [index: string]: RegistrationFormComponent } =
                {
                    details: {
                        type: RegistrationComponent.RegistrationElement,
                        component: (
                            <>
                                <h1>
                                    Details
                                </h1>
                                <div className="card card-shadow">
                                    <div className="form-row pt-4 pb-2 px-4">
                                        <div className="col-12">
                                            {renderFormControls(
                                                form,
                                                "monitoringIssueDocument",
                                                {
                                                    name: {
                                                        type: RegistrationComponent.RegistrationControl,
                                                        inputType: TextFormInput,
                                                        inputDisabled: formDisabled,
                                                        groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                        labelClassName: "col-form-label col-2",
                                                        fieldClassName: "col-10 generic-form-field mr-0 pr-0",
                                                    },
                                                },
                                                lookups,
                                                validationFailures,
                                                formProperties,
                                                changeForm
                                            )}
                                        </div>
                                    </div>
                                    <div className="form-row pt-2 pb-4 px-4">
                                        <div className="col-12">
                                            {renderFormControls(
                                                form,
                                                "monitoringIssueDocument",
                                                {
                                                    location: {
                                                        type: RegistrationComponent.RegistrationControl,
                                                        inputType: TextFormInput,
                                                        inputDisabled: formDisabled,
                                                        inputProps: {
                                                            addOnRightButton: (
                                                                <button
                                                                    className="btn btn-secondary"
                                                                    id="button-addon2"
                                                                    type="button"
                                                                    style={{
                                                                        padding: "4px 10px",
                                                                        cursor: form?.location == null || !isUrl(form?.location)
                                                                                ? "not-allowed"
                                                                                : "pointer",
                                                                    }}
                                                                    disabled={form?.location == null || !isUrl(form?.location)}
                                                                    onClick={() => {
                                                                        openFile(form.location);
                                                                    }}
                                                                >
                                                                    <FontAwesomeIcon icon={FontAwesomeIcons.Duotone.ARROWUPRIGHTFROMSQUARE} />
                                                                </button>
                                                            ),
                                                        },
                                                        groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                        labelClassName: "col-form-label col-2",
                                                        fieldClassName: "col-10 generic-form-field mr-0 pr-0",
                                                    },
                                                },
                                                lookups,
                                                validationFailures,
                                                formProperties,
                                                changeForm
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </>
                        ),
                    },
                };

            return (
                <Form
                    model={reduxFormName}
                    onSubmit={(val, _event) => handleOnSubmit(val)}
                    onSubmitFailed={(val) => handleOnSubmitFailed(val)}
                >
                    {renderFormControls(
                        form,
                        "monitoringIssueDocument",
                        formComponents,
                        lookups,
                        validationFailures,
                        formProperties,
                        changeForm
                    )}
                    <div className="mt-4 mb-4 text-right">
                        <button
                            disabled={formDisabled}
                            onClick={() => {
                                changeForm(reduxFormName + ".saveAndReturn", false);
                            }}
                            type="submit"
                            className="btn btn-primary mr-2"
                        >
                            Submit
                        </button>
                        <button
                            disabled={formDisabled}
                            onClick={() => {
                                changeForm(reduxFormName + ".saveAndReturn", true);
                            }}
                            type="submit"
                            className="btn btn-primary mr-2"
                        >
                            Submit And Return
                        </button>
                        <button
                            onClick={onCancel}
                            className="btn btn-primary mr-2"
                        >
                            Cancel
                        </button>
                    </div>
                </Form>
            );
        }
        return null;
    }, [
        permissions,
        lookups,
        form,
        permissions,
        onCancel,
        handleOnSubmit,
        handleOnSubmitFailed,
        loadingMonitoringIssueDocument,
        savingMonitoringIssueDocument,
        creatingMonitoringIssueDocument,
        formState,
        monitoringIssueDocument
    ]);

    return (
        <RestrictedLayout
            loading={
                loadingMonitoringIssueDocument || 
                creatingMonitoringIssueDocument ||
                loadingTrialInstitution
            }
            permission={Dtos.Permission.MonitoringIssueDocumentView}
        >
            { renderContent }
        </RestrictedLayout>
    )
};

const mapStateToProps = (state: ITmdState): Partial<MonitoringIssueDocumentPageProps> => {
    let monitoringIssueDocument: Dtos.MonitoringIssueDocument | undefined = !(state.monitoringIssueDocument.formData instanceof Array)
        ? state.monitoringIssueDocument.formData
        : undefined;
    let trialInstitution: Dtos.TrialInstitution | undefined = !(state.trialInstitution.formData instanceof Array)
        ? state.trialInstitution.formData
        : undefined;
    let permissions = state.user.userSummary ? state.user.userSummary.permissions : [];

    return {
        monitoringIssueDocument: monitoringIssueDocument!,
        loadingMonitoringIssueDocument: state.monitoringIssueDocument.loadState && state.monitoringIssueDocument.loadState.status == RequestState.Pending,
        loadMonitoringIssueDocumentSuccess: state.monitoringIssueDocument.loadState && state.monitoringIssueDocument.loadState.status == RequestState.Success,
        loadMonitoringIssueDocumentFailure: state.monitoringIssueDocument.loadState && state.monitoringIssueDocument.loadState.status == RequestState.Failure,
        loadMonitoringIssueDocumentErrorCode: state.monitoringIssueDocument.loadState.errorCode,
        savingMonitoringIssueDocument: state.monitoringIssueDocument.saveState && state.monitoringIssueDocument.saveState.status == RequestState.Pending,
        saveMonitoringIssueDocumentSuccess: state.monitoringIssueDocument.saveState && state.monitoringIssueDocument.saveState.status == RequestState.Success,
        saveMonitoringIssueDocumentFailure: state.monitoringIssueDocument.saveState && state.monitoringIssueDocument.saveState.status == RequestState.Failure,
        creatingMonitoringIssueDocument: state.monitoringIssueDocument.createState && state.monitoringIssueDocument.createState.status == RequestState.Pending,
        createMonitoringIssueDocumentSuccess: state.monitoringIssueDocument.createState && state.monitoringIssueDocument.createState.status == RequestState.Success,
        createMonitoringIssueDocumentFailure: state.monitoringIssueDocument.createState && state.monitoringIssueDocument.createState.status == RequestState.Failure,
        validatingMonitoringIssueDocument: state.monitoringIssueDocument.validateState && state.monitoringIssueDocument.validateState.status == RequestState.Pending,
        permissions: permissions,
        lookups: state.monitoringIssueDocument.lookups,
        form: state.reduxForms.monitoringIssueDocument,
        formState: state.monitoringIssueDocument.formState,
        formProperties: state.monitoringIssueDocument.formProperties,
        validationFailures: monitoringIssueDocument
            ? state.monitoringIssueDocument.validationFailures
            : undefined,
        trialInstitution: trialInstitution!,
        loadingTrialInstitution: state.trialInstitution.loadState && state.trialInstitution.loadState.status == RequestState.Pending,
        loadTrialInstitutionSuccess: state.trialInstitution.loadState && state.trialInstitution.loadState.status == RequestState.Success,
        loadTrialInstitutionFailure: state.trialInstitution.loadState && state.trialInstitution.loadState.status == RequestState.Failure,
        loadTrialInstitutionErrorCode: state.trialInstitution.loadState.errorCode,
    };
};

const mapDispatchToProps = (dispatch): Partial<MonitoringIssueDocumentPageProps> => {
    return {
        loadForm: bindActionCreators(actions.load, dispatch),
        changeForm: bindActionCreators(actions.change, dispatch),
        resetForm: bindActionCreators(actions.reset, dispatch),
        loadMonitoringIssueDocument: bindActionCreators(MonitoringIssueDocumentActions.LoadMonitoringIssueDocumentById, dispatch),
        clearMonitoringIssueDocument: bindActionCreators(MonitoringIssueDocumentActions.Clear, dispatch),
        saveMonitoringIssueDocument: bindActionCreators(MonitoringIssueDocumentActions.SaveMonitoringIssueDocument, dispatch),
        createMonitoringIssueDocument: bindActionCreators(MonitoringIssueDocumentActions.CreateMonitoringIssueDocument, dispatch),
        validateMonitoringIssueDocument: bindActionCreators(MonitoringIssueDocumentActions.ValidateMonitoringIssueDocument, dispatch),
        setBreadcrumbs: bindActionCreators(NavigationActions.SetBreadcrumbs, dispatch),
        setDrawerItems: bindActionCreators(NavigationActions.SetDrawerItems, dispatch),
        loadTrialInstitution: bindActionCreators(TrialInstitutionActions.LoadTrialInstitutionById, dispatch),
        clearTrialInstitution: bindActionCreators(TrialInstitutionActions.Clear, dispatch),
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(MonitoringIssueDocumentPage);